import { Button } from '@/components/common/button'
import { TokenImage } from '@/components/common/tokenImage'
import { Tooltip, TooltipContent } from '@/components/common/tooltip'
import { WalletIcon } from '@/components/icons/wallet'
import { cn } from '@/utils/cn'
import { type InputHTMLAttributes, type ReactNode, forwardRef, useId } from 'react'

interface Props {
  actionDescription?: string
  usdValue: string
  balance?: string
  asset: { symbol: string }
  tokenSelector?: ReactNode
  hasError?: boolean
  className?: string
  onBalanceClick?: () => void
}

type TxInputProps = Props & InputHTMLAttributes<HTMLInputElement>

export const TxInput = forwardRef<HTMLInputElement, TxInputProps>(
  (
    {
      actionDescription,
      usdValue,
      balance,
      asset,
      tokenSelector,
      hasError,
      className,
      onBalanceClick,
      id: idProp,
      ...inputProps
    },
    ref
  ) => {
    const generatedId = useId()
    const inputId = idProp ?? generatedId

    return (
      <div
        className={cn(
          'relative flex flex-col gap-2 rounded-2xl border bg-surface p-4',
          'transition-colors',
          'focus-within:border-focus focus-within:ring-2 focus-within:ring-inset',
          hasError ? 'border-destructive focus-within:border-destructive' : 'border-border',
          className
        )}
      >
        <label htmlFor={inputId} className="absolute inset-0 cursor-text" />

        {actionDescription && <p className="text-body-foreground text-sm">{actionDescription}</p>}

        <div className="relative flex items-center justify-between gap-2">
          <input
            id={inputId}
            ref={ref}
            inputMode="decimal"
            placeholder="0.00"
            autoComplete="off"
            className={cn(
              'min-w-0 flex-1 bg-transparent font-semibold text-5xl outline-none',
              'text-active-foreground placeholder:text-placeholder-foreground/55'
            )}
            {...inputProps}
          />
          <div className="relative flex shrink-0 items-center gap-1">
            {tokenSelector ?? (
              <Tooltip tabIndex={-1}>
                <div className="flex shrink-0 items-center gap-1">
                  <TokenImage symbol={asset.symbol} className="size-7 bg-page p-0.5" />
                  <span className="font-medium text-body-foreground text-sm">{asset.symbol}</span>
                </div>
                <TooltipContent>
                  <div className="flex items-center gap-3">
                    <TokenImage symbol={asset.symbol} className="size-7" />
                    <p className="font-bold">{asset.symbol}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between text-body-foreground text-sm">
          <span>{usdValue}</span>
          {balance && (
            <Button
              variant="default"
              size="default"
              type="button"
              disabled={!onBalanceClick}
              onClick={onBalanceClick}
              className={cn('relative flex items-center gap-1 rounded-xl')}
            >
              <WalletIcon size={20} />
              <span>
                Balance: {balance} {asset.symbol}
              </span>
            </Button>
          )}
        </div>
      </div>
    )
  }
)
TxInput.displayName = 'TxInput'
